import type { IconName } from '@/components/ui/icons';
import {
  createShowcaseProjects,
  createShowcaseTasks,
  createShowcaseUsers,
} from '@/demo/showcaseCatalog';
import { canResolve } from '@/lib/auth/scopedCapabilityResolver';
import {
  buildUserAuthState,
  getActiveScopedRelationships,
} from '@/lib/auth/scopedRelationshipResolver';
import { customerRouteHref } from '@/lib/navigation/canonicalRoutes';
import type { User } from '@/types';

export interface AuthorizedSearchResult {
  id: string;
  type: 'project' | 'task' | 'user' | 'document';
  title: string;
  subtitle?: string;
  description?: string;
  icon: IconName;
  url: string;
  relevance: number;
}

interface BuildAuthorizedDemoSearchResultsInput {
  user: User | null | undefined;
  workspaceId: string | null | undefined;
  searchTerm: string;
  isDemoMode: boolean;
}

/**
 * Fail-closed adapter for the local showcase catalog.
 *
 * The catalog is never a production search provider. Even in Demo, results
 * are reduced to the current workspace and the authenticated user's exact
 * account/resource authority before text matching occurs.
 */
export function buildAuthorizedDemoSearchResults({
  user,
  workspaceId,
  searchTerm,
  isDemoMode,
}: BuildAuthorizedDemoSearchResultsInput): AuthorizedSearchResult[] {
  const normalizedTerm = searchTerm.trim().toLocaleLowerCase();
  if (!isDemoMode || !user || !workspaceId || !normalizedTerm) return [];

  const authState = buildUserAuthState(user);
  const projects = createShowcaseProjects().filter((project) =>
    canResolve(authState, 'project.view', { workspaceId, projectId: project.id }),
  );
  const projectNames = new Map(projects.map((project) => [project.id, project.name]));
  const visibleProjectIds = new Set(projects.map((project) => project.id));
  const exactTaskIds = new Set(
    getActiveScopedRelationships(user, 'task').map((relationship) => relationship.resourceId),
  );

  const tasks = createShowcaseTasks().filter((task) =>
    exactTaskIds.has(task.id)
      || (visibleProjectIds.has(task.projectId)
        && canResolve(authState, 'task.view', {
          workspaceId,
          projectId: task.projectId,
          taskId: task.id,
          taskAssigneeId: task.assigneeId,
          taskCreatorId: task.createdBy,
        })),
  );

  // People search is an organization-administration surface. Project-scoped
  // collaborator search needs a separate server projection and therefore
  // fails closed here instead of exposing the demo-wide identity catalog.
  const canSearchWorkspaceUsers = user.account_role === 'organization_owner'
    || user.account_role === 'organization_admin';
  const users = canSearchWorkspaceUsers
    ? createShowcaseUsers().filter((candidate) => candidate.workspace_id === workspaceId)
    : [];

  const results: AuthorizedSearchResult[] = [
    ...projects.map((project) => ({
      id: project.id,
      type: 'project' as const,
      title: project.name,
      subtitle: project.primaryClientCompany?.name,
      description: project.description,
      icon: 'folderOpen' as const,
      url: customerRouteHref(workspaceId, 'project', {
        resourceId: project.id,
        resourceWorkspaceId: workspaceId,
      }),
      relevance: 0.95,
    })),
    ...tasks.map((task) => ({
      id: task.id,
      type: 'task' as const,
      title: task.title,
      subtitle: projectNames.get(task.projectId),
      description: task.description,
      icon: 'checkSquare' as const,
      url: customerRouteHref(workspaceId, 'tasks', { search: { taskId: task.id } }),
      relevance: 0.88,
    })),
    ...users.map((candidate) => ({
      id: candidate.id,
      type: 'user' as const,
      title: candidate.name,
      subtitle: candidate.department,
      description: candidate.email,
      icon: 'users' as const,
      url: customerRouteHref(workspaceId, 'users', { search: { userId: candidate.id } }),
      relevance: 0.82,
    })),
  ];

  return results
    .filter((item) =>
      item.title.toLocaleLowerCase().includes(normalizedTerm)
      || item.subtitle?.toLocaleLowerCase().includes(normalizedTerm)
      || item.description?.toLocaleLowerCase().includes(normalizedTerm),
    )
    .sort((a, b) => b.relevance - a.relevance);
}
